import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { notificationService } from '../../services/notificationService';
import { 
  NOTIFICATION_TYPE_LABELS,
  RESPONSE_TYPES 
} from '../../constants/notificationConstants';
import { dateUtils } from '../../utils/helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import NotificationStatusModal from './NotificationStatusModal';
import Input from '../ui/Input';
import Select from '../ui/Select';

const NotificationHistory = () => {
  const { user } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  // Load notifications on component mount
  useEffect(() => {
    loadNotifications();
  }, [user]);

  const loadNotifications = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading notifications for user:', user.uid);
      console.log('User object:', user);
      
      // TEMPORARY FIX: Since existing notifications have senderId: undefined,
      // let's load all notifications for now
      const notificationData = await notificationService.getNotificationHistory(null);
      
      console.log('Loaded notifications:', notificationData);
      setNotifications(notificationData);
      
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Failed to load notifications: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter notifications based on search and type
  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = !searchTerm || 
      notification.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.meetingHeading?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = !typeFilter || notification.type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  // Calculate response statistics for a notification
  const getResponseStats = (notification) => {
    const responses = notification.responses || [];
    const total = notification.recipientIds?.length || 0;
    
    const stats = {
      total,
      accept: responses.filter(r => r.response === RESPONSE_TYPES.ACCEPT).length,
      reject: responses.filter(r => r.response === RESPONSE_TYPES.REJECT).length,
      tentative: responses.filter(r => r.response === RESPONSE_TYPES.TENTATIVE).length,
      pending: total - responses.filter(r => r.response !== RESPONSE_TYPES.PENDING).length
    };
    
    stats.responseRate = total > 0 ? Math.round(((total - stats.pending) / total) * 100) : 0;
    
    return stats;
  };

  const handleViewStatus = (notification) => {
    setSelectedNotification(notification);
    setShowStatusModal(true);
  };

  const getTypeIcon = (type) => {
    const icons = {
      general: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.959 8.959 0 01-2.4-.32l-3.2 2.88A1 1 0 015 21V9a8 8 0 018-8c4.418 0 8 3.582 8 8z" />
        </svg>
      ),
      invitation: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      meeting: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.121M7 20v-2c0-.656.126-1.283.356-1.857M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    };
    return icons[type] || icons.general;
  };

  const getTypeColor = (type) => {
    const colors = {
      general: 'text-accent bg-accent-subtle',
      invitation: 'text-success bg-success-subtle',
      meeting: 'text-warning bg-warning-subtle'
    };
    return colors[type] || colors.general;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-secondary flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading notification history..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-secondary">
      <div className={`max-w-6xl mx-auto ${containerPadding}`}>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className={`${titleSize} font-bold text-text-primary`}>
              Notification History
            </h1>
            <div className="text-sm text-text-secondary">
              {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
            </div>
          </div>
          
          {/* Filters */}
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
            <Input
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-base"
            />
            
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              options={[
                { value: '', label: 'All Types' },
                { value: 'general', label: 'General' },
                { value: 'invitation', label: 'Invitation' },
                { value: 'meeting', label: 'Meeting' }
              ]}
              className="input-base"
              placeholder="Filter by type"
            />
          </div>
          
          {/* Refresh Button */}
          <div className="mt-4">
            <button
              onClick={loadNotifications}
              disabled={loading}
              className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-error-subtle border border-border-error rounded-xl">
            <div className="text-error font-medium">Error loading notifications</div>
            <div className="text-error text-sm mt-1">{error}</div>
            <button
              onClick={loadNotifications}
              className="mt-2 px-3 py-1 bg-error text-white rounded text-sm hover:bg-error-hover"
            >
              Retry
            </button>
          </div>
        )}

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <div className="bg-surface-elevated rounded-xl border border-border-primary p-8">
            <EmptyState
              icon={
                <svg className="w-16 h-16 text-text-quaternary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 17h5l-5 5V12a1 1 0 00-1-1h-1m-1 1v5a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h6m2 0V4a1 1 0 011-1h1a1 1 0 011 1v1m0 0h2" />
                </svg>
              }
              title={searchTerm || typeFilter ? "No notifications found" : "No notifications sent yet"}
              description={
                searchTerm || typeFilter
                  ? "Try adjusting your search or filter criteria"
                  : "Notifications you send will appear here with recipient status tracking"
              }
            />
          </div>
        ) : (
          <div className="space-y-4">
            {filteredNotifications.map(notification => {
              const stats = getResponseStats(notification);
              
              return (
                <div
                  key={notification.id}
                  className="bg-surface-elevated rounded-xl border border-border-primary p-6 hover:shadow-theme-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center space-x-3 mb-3">
                        <div className={`p-2 rounded-lg ${getTypeColor(notification.type)}`}>
                          {getTypeIcon(notification.type)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-text-primary">
                            {notification.type === 'meeting' 
                              ? notification.meetingHeading 
                              : NOTIFICATION_TYPE_LABELS[notification.type]
                            }
                          </h3>
                          <p className="text-sm text-text-secondary">
                            Sent {dateUtils.formatDateTime(notification.sentAt)} to {stats.total} recipients
                          </p>
                        </div>
                      </div>

                      {/* Content Preview */}
                      <p className="text-text-secondary text-sm mb-4 line-clamp-2">
                        {notification.content}
                      </p>

                      {/* Event Details */}
                      {(notification.venue || notification.eventDate) && (
                        <div className="flex flex-wrap gap-4 text-xs text-text-tertiary mb-4">
                          {notification.venue && (
                            <div className="flex items-center space-x-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              </svg>
                              <span>{notification.venue}</span>
                            </div>
                          )}
                          {notification.eventDate && (
                            <div className="flex items-center space-x-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>
                                {dateUtils.formatDate(notification.eventDate)}
                                {notification.eventTime && ` at ${notification.eventTime}`}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Response Statistics */}
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center space-x-1">
                          <span className="text-sm text-text-secondary">Response Rate:</span>
                          <span className="font-medium text-text-primary">{stats.responseRate}%</span>
                        </div>
                        
                        {notification.type !== 'general' && (
                          <>
                            {stats.accept > 0 && (
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-success rounded-full"></div>
                                <span className="text-sm text-success">{stats.accept} Accept</span>
                              </div>
                            )}
                            {stats.tentative > 0 && (
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-warning rounded-full"></div>
                                <span className="text-sm text-warning">{stats.tentative} Tentative</span>
                              </div>
                            )}
                            {stats.reject > 0 && (
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-error rounded-full"></div>
                                <span className="text-sm text-error">{stats.reject} Reject</span>
                              </div>
                            )}
                          </>
                        )}
                        
                        {stats.pending > 0 && (
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-info rounded-full"></div>
                            <span className="text-sm text-info">{stats.pending} Pending</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleViewStatus(notification)}
                        className="px-4 py-2 text-sm font-medium text-accent hover:text-accent-hover hover:bg-accent-subtle rounded-lg transition-colors"
                      >
                        View Status
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Status Modal */}
        {showStatusModal && selectedNotification && (
          <NotificationStatusModal
            isOpen={showStatusModal}
            onClose={() => {
              setShowStatusModal(false);
              setSelectedNotification(null);
            }}
            notification={selectedNotification}
          />
        )}
      </div>
    </div>
  );
};

export default NotificationHistory;